<template>
  <div class="chart-bar">
    <div class="bar-header">
      <div class="bar-title">
        <BaseIcon
          v-if="hasWritePermission && item.widgetName"
          class="cus-home-drag-n-drop-pointer"
          name="dragNDrop"
          width="0.813rem"
          height="1.25rem"
        />
        <BaseIcon
          :name="item.header.titleIcon"
          width="1em"
          height="1em"
          v-if="item.header.titleIcon"
          class="icon-margin"
        />
        {{ item.header.title }}
        <span
          v-if="hasWritePermission"
          @click="deleteWidget"
          class="delete-icon"
        >
          <BaseIcon
            name="deleteFilled"
            width=".9125rem"
            height=".9125rem"
            v-if="item.header.enableDeleteWidget"
          />
        </span>
      </div>
      <div
        class="bar-content-error"
        v-if="item.header.isError && childSource.length > 0"
      >
        <!-- {{childSource.length}} -->
        <span class="bar-error-percentage">
          {{ getChilds('percentage', childSource) }}%
        </span>
        <span class="bar-errors">
          {{ getChilds('total', childSource) }}
          {{ $t('bm.errors') }}
        </span>
        <span>
          {{ getChilds('pm', childSource) }}
          {{ $t('apm.errors-per-min') }}
        </span>
      </div>
      <div
        v-else-if="item.header.displayOnlyUnit && childSource.length > 0"
        class="bar-content"
      >
        {{ getChilds('total', childSource) }} {{ item.unit }}
      </div>
      <div v-else class="bar-content">
        <div
          v-if="!item.header.hideCenterMiscContent && childSource.length > 0"
          class="bar-content"
        >
          <span class="bar-content-calls">
            {{ getChilds('total', childSource) }} {{ $t('bm.calls') }}
          </span>
          <span>
            {{ getChilds('pm', childSource) }} {{ $t('callsPerMin') }}
          </span>
        </div>
      </div>
      <div class="bar-icon">
        <div v-if="item.header.enableCapabilityInfo">
          <span class="icon-capability">
            <BaseIcon
              :name="item.capability.toLowerCase()"
              width=".9125rem"
              height=".9125rem"
            />
          </span>
          <span class="headings">
            {{ getCapability(item.capability) }}
          </span>
        </div>
        <router-link
          v-if="item.header.displayCustomButton"
          :to="{
            name: 'trace',
            query: {
              type: item.header.customButtonProp.key,
              entity: item.entityType,
            },
          }"
        >
          <div class="custom-button">
            {{ item.header.customButtonProp.title }}
          </div>
        </router-link>
        <div
          v-if="item.header.enableBarChartRedirect"
          class="appName"
          @click="() => redirectAppInput(item.capability)"
        >
          {{ item.serviceName }}
        </div>
        <div v-if="!item.header.hideCustomToolbarButtons">
          <span
            class="icon-class"
            @click="handleZoomOut"
            :id="`${replaceSpaceInId(item.header.title)}-zoom-out`"
          >
            <BaseIcon name="zoomOut" width="2.438em" height="2.438em" />
          </span>
          <span
            class="icon-class"
            @click="handleZoomIn"
            :id="`${replaceSpaceInId(item.header.title)}-zoom-in`"
          >
            <BaseIcon name="zoomIn" width="2.438em" height="2.438em" />
          </span>
          <span
            class="icon-class"
            @click="handleReset"
            :id="`${replaceSpaceInId(item.header.title)}-zoom-reset`"
          >
            <BaseIcon name="reset" width="1.438em" height="1.438em" />
          </span>
        </div>
      </div>
    </div>
    <div class="chart-body">
      <apexchart
        height="100%"
        type="bar"
        :options="updateOptions"
        :series="series"
        ref="chart"
      ></apexchart>
      <div class="progress-bar" v-if="item.progressBar">
        <P class="progress-title"> Capacity used ({{ progressValue }}%) </P>
        <div class="progress-wrapper">
          <ProgressBar
            class="progress-usage"
            :progress="progressValue"
            :variant="'range-color'"
            :showUsageValue="false"
            :color="item.chartColor[0]"
          />
          <span class="progress-used-val">
{{ usedValue }}
</span>&nbsp;
          &nbsp;<span class="progress-unit">
            {{ usedUnit }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import BaseIcon from '@/rex-components/BaseIcon.vue';
import shortenLargeNumber from '@/utils/numberShortner';
import { replaceSpaceInId } from '../../utils/helper-function';
import { isNumber } from 'lodash';
import {
  CAPABILITY_APM,
  CAPABILITY_BROWSER,
  CAPABILITY_MOBILE,
} from '@/utils/constants';
import {
  ToolTip,
  Legend,
  Markers,
  ChartToolBar,
  Animations,
  XAxisConfig,
  YAxisConfig,
  ChartFill,
  Grid,
  dashLinesize,
  lineWidth,
  tooltipValues,
  getCustomTooltipContent,
} from '@/components/charts/charts.global.config';
import { mapMutations, mapGetters, mapState } from 'vuex';
import { cusGraphCapabilityLabelMapper } from '@/components/charts/util';
import { CAPABILITY_ANALYTICS, CAPABILITY_LOG } from '@/utils/constants';
import { VIEW_SAVED_SEARCHES_TAB_KEY } from '@/components/Log/log.constants';
import ProgressBar from '@/components/APM/Dashboard/Instance/ProgressBar.vue';
import { usePermissions } from '@/hooks/usePermissions';
import { HOME_RESOURCE_GROUP } from '@/router/constant';

export default {
  components: { BaseIcon, ProgressBar },
  name: 'ApexChartBar',
  props: {
    data: {},
    type: {},
    intervalTime: {},
    item: {},
    index: {},
    childSource: {},
    customButtonVisible: Boolean,
    customButtonTextIfVisiable: String,
    compareIntervalTime: {
      type: Array,
      default: () => [],
    },
  },
  data: function () {
    return {
      minX: 1,
      maxX: this.intervalTime.length,
      progressValue: 0,
      usedValue: 0,
      usedUnit: 'bytes',
      isCompare: true,
      compared: true,
      options: {
        chart: {
          id: `vuechart-bar-${this.index}`,
          type: this.compared ? 'line' : 'bar',
          group:
            typeof this.item.isSynchronized === 'undefined' ||
            this.item.isSynchronized
              ? 'group'
              : '',
          animations: Animations,
          background: 'inherit',
          events: {
            zoomed: (chartContext, { xaxis, yaxis }) => {
              this.updateMinMax(xaxis);
            },
            beforeMount: (chartContext, config) => {
              // console.log('config', config);
              //  this.setConfig(config);
            },
          },
          toolbar: ChartToolBar,
        },
        tooltip: ToolTip,
        grid: Grid,
        colors: this.item.chartColor,
        xaxis: {
          ...XAxisConfig,
          tickAmount: this.intervalTime.length > 14 ? 5 : 3,
          title: {
            text: this.item.axisLabels.x,
            ...XAxisConfig.title,
          },
          min: this.intervalTime[0],
        },
        yaxis: {
          ...YAxisConfig,
          title: {
            text: this.item.axisLabels.y,
            ...YAxisConfig.title,
          },
        },
        legend: Legend,
        markers: Markers,
        fill: ChartFill,
        plotOptions: {
          bar: {
            // columnWidth: '22em',
            horizontal: false,
            endingShape: 'rounded',
            borderRadius: 10,
          },
        },
        dataLabels: {
          enabled: true,
          style: {
            colors: ['rgba(255,255, 255, 0)'],
          },
        },
      },
    };
  },
  methods: {
    ...mapMutations('homeState', ['setdeleteWidgetId']),
    getCapability(capability) {
      return cusGraphCapabilityLabelMapper[capability] || capability;
    },
    deleteWidget() {
      this.setdeleteWidgetId(this.item.id);
    },
    resize() {
      const chart = this.$refs.chart;
      chart.myChart.resize();
    },
    setConfig(config) {
      // console.log('configss', config.globals.maxX);
      // this.minX = config.globals.maxX;
      // this.maxX = config.globals.maxX;
    },
    handleZoomIn() {
      // Find minMax for x

      try {
        if (this.$refs.chart.chart.w.globals.minX)
          this.minX = this.$refs.chart.chart.w.globals.minX;
        if (this.$refs.chart.chart.w.globals.maxX)
          this.maxX = this.$refs.chart.chart.w.globals.maxX;
      } catch (error) {
        // Todo: Handle error
      }

      if (
        typeof this.minX === 'undefined' &&
        typeof this.maxX === 'undefined'
      ) {
        this.handleReset();
      }
      if (this.maxX - this.minX > 2) {
        const centerX = (this.minX + this.maxX) / 2;
        let newMinX = (this.minX + centerX) / 2;
        let newMaxX = (this.maxX + centerX) / 2;
        const newMinXMaxX = this._getNewMinXMaxX(newMinX, newMaxX);
        this.minX = newMinXMaxX.minX;
        this.maxX = newMinXMaxX.maxX;
        this.$refs.chart.zoomX(this.minX, this.maxX);
      }
    },
    handleReset() {
      this.$refs.chart.zoomX();
      this.minX = 1;
      this.maxX = this.intervalTime.length;
    },
    _getNewMinXMaxX(newMinX, newMaxX) {
      return {
        minX: Math.floor(newMinX),
        maxX: Math.floor(newMaxX),
      };
    },
    updateMinMax(xaxis) {
      this.minX = xaxis.min;
      this.maxX = xaxis.max;
    },
    handleZoomOut() {
      //Find minMax for x
      // This checks if min & max are undefined and resets them

      try {
        if (this.$refs.chart.chart.w.globals.minX)
          this.minX = this.$refs.chart.chart.w.globals.minX;
        if (this.$refs.chart.chart.w.globals.maxX)
          this.maxX = this.$refs.chart.chart.w.globals.maxX;
      } catch (error) {
        // Todo: Handle error
      }

      if (
        typeof this.minX === 'undefined' &&
        typeof this.maxX === 'undefined'
      ) {
        this.handleReset();
      }
      if (this.minX > 0 && this.maxX - this.minX >= 1) {
        const centerX = (this.minX + this.maxX) / 2;
        let newMinX = this.minX - (centerX - this.minX);
        let newMaxX = this.maxX - (centerX - this.maxX);
        const newMinXMaxX = this._getNewMinXMaxX(newMinX, newMaxX);
        this.minX = newMinXMaxX.minX;
        this.maxX = newMinXMaxX.maxX;
        this.$refs.chart.zoomX(this.minX, this.maxX);
      }
    },
    getChilds(type, val) {
      if (type === 'pm') {
        const item = val.find((o) => o.metricType === 'pm');
        if (item) {
          return shortenLargeNumber(item.avgNum, 1);
        } else return 0;
      } else if (type === 'percentage') {
        const item = val.find((o) => o.metricType === 'percentage');
        if (item) {
          return item.avgNum ? item.avgNum / 100 : 0;
        } else return 0;
      } else if (type === 'total') {
        const item = val.find((o) => o.metricType === 'total');
        if (item) {
          if (item.name === 'service_resp_time') {
            return item.avgNum;
          } else return shortenLargeNumber(item.avgNum, 1);
        } else return 0;
      } else return 0;
    },
    replaceSpaceInId,
    redirectAppInput(capability) {
      switch (capability) {
        case CAPABILITY_LOG:
          this.$router.push({
            name: 'log',
            query: { currView: VIEW_SAVED_SEARCHES_TAB_KEY },
          });
          break;
        case CAPABILITY_ANALYTICS:
          this.$router.push({
            name: 'analytics',
            query: { currView: VIEW_SAVED_SEARCHES_TAB_KEY },
          });
          break;
      }
    },
  },
  computed: {
    ...mapGetters('globalState/settings', [
      'getCompareDurationTime',
      'getIsCompareOptionsSelected',
    ]),
    ...mapState('globalState/app', [
      'compareSupportedCapabilities',
      'currentSideNav',
    ]),
    getDefaultTooltip() {
      return {
        ...tooltipValues,
      };
    },
    getCustomTooltip() {
      if (
        !this.compareSupportedCapabilities.includes(this.currentSideNav.key)
      ) {
        return this.options.tooltip;
      } else {
        return {
          ...tooltipValues,
          custom: ({ series, seriesIndex, dataPointIndex, w }) => {
            return getCustomTooltipContent(
              series,
              this.data,
              this.intervalTime,
              this.compareIntervalTime,
              this.item,
              dataPointIndex,
              w,
            );
          },
        };
      }
    },
    series() {
      const keys = Object.keys(this.data || {}).filter(
        (i) => Array.isArray(this.data[i]) && this.data[i].length,
      );
      const nonComparedSeries = keys.filter((key) => !key.includes('compared'));
      const comparedSeries = keys.filter((key) => key.includes('compared'));

      const sortedKeys = [...nonComparedSeries, ...comparedSeries];
      const legendName = (isCompared, isCompareOptionsSelected, itemName) => {
        const compareItemName = itemName.replace(/_compared$/, '');
        return isCompared
          ? isCompareOptionsSelected
            ? `${this.$t('previousPeriod')} - ${compareItemName}`
            : `${this.$t('currentPeriod')} - ${compareItemName}`
          : isCompareOptionsSelected
            ? `${this.$t('currentPeriod')} - ${compareItemName}`
            : compareItemName;
      };
      const series = sortedKeys.map((i) => {
        const isCompared = i.includes('compared');
        return {
          name: legendName(isCompared, this.getIsCompareOptionsSelected, i),
          type: isCompared ? 'line' : 'column',
          data: this.intervalTime.map((x) => {
            const itemIndex = this.intervalTime.indexOf(x);
            return {
              x: x,
              y: itemIndex !== -1 ? this.data[i][itemIndex] : null,
            };
          }),
        };
      });
      return series;
    },
    updateOptions() {
      const keys = Object.keys(this.data || {}).filter(
        (i) => Array.isArray(this.data[i]) && this.data[i].length,
      );
      const nonComparedSeries = keys.filter((key) => !key.includes('compared'));
      const comparedSeries = keys.filter((key) => key.includes('compared'));

      const sortedKeys = [...nonComparedSeries, ...comparedSeries];
      const dashArray = [];
      const widthArray = [];
      sortedKeys.map((i) => {
        const isCompared = i.includes('compared');
        dashArray.push(isCompared ? dashLinesize : 0);
        widthArray.push(lineWidth);
      });
      const customLegendFormatter = (seriesName, opts) => {
        const seriesIndex = opts.seriesIndex;
        const seriesColor = this.updateOptions.colors[seriesIndex];
        const isPreviousPeriod = seriesName.includes(
          `${this.$t('previousPeriod')}`,
        );
        const isCurrentPeriod = seriesName.includes(
          `${this.$t('currentPeriod')}`,
        );
        const circleStyle = isPreviousPeriod
          ? `border-color: ${seriesColor}; border-style: dashed;`
          : `background-color: ${seriesColor};`;

        const circleClass = isPreviousPeriod
          ? 'legend-dashed-circle'
          : 'legend-circle';
        return `<span class="${circleClass}" style="${circleStyle}"></span> ${seriesName}`;
      };

      const newLegendOptions = {
        ...this.options.legend,
        show: true,
        markers: {
          width: 0,
          height: 0,
        },
        formatter: customLegendFormatter,
      };
      const updatedTemp = {
        ...this.options,
        colors:
          this.getIsCompareOptionsSelected &&
          this.compareSupportedCapabilities.includes(this.currentSideNav.key)
            ? [...this.item.chartColor, ...this.item.chartColor]
            : [...this.item.chartColor],
        stroke:
          this.getIsCompareOptionsSelected &&
          this.compareSupportedCapabilities.includes(this.currentSideNav.key)
            ? {
                dashArray,
                width: widthArray,
              }
            : {
                dashArray: [],
                width: 0,
              },
        legend: newLegendOptions,
        tooltip:
          this.getIsCompareOptionsSelected &&
          this.compareSupportedCapabilities.includes(this.currentSideNav.key)
            ? this.getCustomTooltip
            : this.options.tooltip,
      };
      return updatedTemp;
    },

    getErrorPercentage() {
      if (this.childSource.totalValueAlt.total == 0) {
        return 0;
      }
      var error_percent =
        (this.childSource.totalValue.total /
          this.childSource.totalValueAlt.total) *
        100;
      if (isNaN(error_percent)) {
        return 0;
      }
      return error_percent.toFixed(2);
    },
    hasWritePermission() {
      const { hasCapabilityWritePermission } = usePermissions();

      return hasCapabilityWritePermission(HOME_RESOURCE_GROUP);
    },
  },
  watch: {
    minX(oldVal, newVal) {
      // console.log('oldval', oldVal);
      // console.log('newval', newVal);
    },
  },
};
</script>
<style lang="scss" scoped>
.chart-body {
  margin-right: 2px;
  margin-left: 0.125em;
  height: 20.625em;
  border-radius: 1.0625em;
}

.chart-bar {
  width: 100%;
  border-radius: 1.0625em;
  background-color: $bg-secondary;
}
.bar-content-error {
  color: $poor;
  margin-left: 1em;
  .bar-error-percentage {
    margin-right: 1.125rem;
  }
  .bar-errors {
    margin-right: 1em;
  }
}
.bar-content {
  margin-left: 1em;
  .bar-content-calls {
    margin-right: 1.3em;
  }
}
.bar-header {
  height: 2.375em;
  border-bottom: 0.0063em solid $border-primary;
  @include flex-center-justify(space-between);
  color: $font-primary;

  .bar-title {
    margin-left: 0.8625em;
    font-family: $semi-bold;
  }
  .bar-icon {
    margin-right: 0.8125em;

    @include flex-center-justify(space-between);
    .icon-capability {
      fill: $font-primary;
      margin-right: 0.3125em;
      height: 1.875em;
    }
    .headings {
      height: 1.875em;
      font-family: $semi-bold;
      font-size: 0.75rem;
      margin-right: 1rem;
      padding-top: 2px;
    }
  }

  .icon-margin {
    margin-right: 0.5em;
  }

  .delete-icon {
    cursor: pointer;
    margin-left: 0.4em;
    margin-top: -0.1em;
    fill: $color-brand-accent;
  }

  .icon-class:hover {
    cursor: pointer;
    svg {
      fill: $font-secondary !important;
    }
  }
  .icon-class:last-child {
    margin-left: 0.3125em;
  }
  .icon-class:first-child {
    margin-right: -0.3125em;
  }
}
.custom-button {
  // margin: 0rem 1.0625em;
  background: $color-brand-accent;
  width: 6.94em;
  height: 1.875em;
  color: $font-contrast;
  // height: 1.92em;
  border-radius: 0.4375em;
  // bar-height: 2.1875em;
  cursor: pointer;
  // font-family: RakutenSansUI-Regular;
  font-size: 0.75em;
  text-align: center;
  align-items: center;
  // bar-height: 1.25em;
  padding-top: 2px;
  &:hover {
    text-decoration: underline;
  }
}
.appName {
  @include flex-center-vert;
  height: 1.875em;
  color: $color-brand-accent;
  cursor: pointer;
  font-family: $semi-bold;
  font-size: 0.75em;
  padding-top: 2px;
  &:hover {
    text-decoration: underline;
  }
}
.cus-home-drag-n-drop-pointer {
  fill: $color-brand-accent;
  margin-right: 0.5rem;
  cursor: pointer;
}
.progress-bar {
  display: flex;
  flex-direction: column;
  margin-left: 2rem;
  align-items: flex-start;
}
.progress-title {
  margin: 0 0 1rem 0;
}
.progress-usage {
  height: 1.5rem;
  width: 50%;
  border-radius: 0.2rem;
  display: flex;
}
:deep(.range) {
  height: 1.5rem;
  border-radius: 0.2rem;
}
.progress-wrapper {
  display: flex;
  width: 95%;
  margin-right: 1rem;
}
.progress-unit {
  color: #c4c4c4;
}
.progress-used-val {
  font-weight: 900;
}
:deep(.legend-circle) {
  display: inline-block;
  height: 12px;
  width: 12px;
  border-width: 0px;
  border-radius: 50%;
}
:deep(.legend-dashed-circle) {
  display: inline-block;
  height: 12px;
  width: 12px;
  border-radius: 50%;
  border: 2px dashed currentColor;
  background-color: transparent;
}
:deep(.tooltip-delta) {
  margin-left: 0.5rem;
}
:deep(.custom-title) {
  font-size: 12px;
  background: black;
  padding: 0.6rem;
}
:deep(.tooltip-text) {
  font-size: 12px;
  text-align: left;
  padding: 4px;
}
.tooltip-section {
  text-align: left;
  margin-bottom: 10px;
}
:deep(.tooltip-series-group) {
  display: flex;
  align-items: center;
}
:deep(.tooltip-marker) {
  margin-right: 5px;
  display: inline-block;
  height: 10px;
  width: 10px;
  border-width: 0px;
  border-radius: 50%;
}
:deep(.series-tooltip-font-group) {
  font-size: 12px;
}
:deep(.series-tooltip-group) {
  font-size: 12px;
  margin-left: 4px;
}
</style>
